import { USE_MOCK } from '@/config'
import { createClient } from '@/api/supabase'
import { mockPartner } from '@/api/mock-partner'
import type { SberAutoLeadPayload } from '@/api/inspection-leads'
import {
  leadPipelineUi,
  leadStatusLabelKey,
  type InspectionLeadStatus,
  type LeadPipeline,
} from '@/types/inspection-lead'
import i18n from '@/utils/i18n'

/** Этапы воронки глазами партнёра: «В работе ОО» склеен с `in_progress`
 *  (partner_status в БД), отделы ФО/ОО партнёру не показываем. */
export function partnerStatuses(pipeline: LeadPipeline): InspectionLeadStatus[] {
  return leadPipelineUi(pipeline).statuses.filter((s) => s !== 'oo_review')
}

/** Подпись статуса для партнёра: `partner.status.*`, иначе — подпись воронки. */
export function partnerStatusLabelKey(status: InspectionLeadStatus, pipeline: LeadPipeline): string {
  const own = `partner.status.${status}`
  return i18n.exists(own) ? own : leadStatusLabelKey('status', status, pipeline, (k) => i18n.exists(k))
}

/**
 * Кабинет партнёра (/partner). Партнёр не читает таблицы: шлюз в БД
 * (check_user_active) пускает его только к своему профилю и RPC partner_*
 * (миграция stryzh 20261103100000_partner_cabinet). Что отдают функции —
 * ровно то, что партнёру положено: без комментариев, чата, звонков, цен,
 * исполнителя и инспектора.
 */

export interface PartnerMe {
  pipeline: LeadPipeline
  pipeline_title: string
  company_name: string | null
  /** partner_operator — заводит заявки; partner_viewer — только смотрит. */
  can_create: boolean
}

export interface PartnerLeadRow {
  id: string
  lead_no: number
  external_id: string | null
  client_name: string | null
  vehicle_brand: string | null
  vehicle_model: string | null
  vehicle_year: number | null
  license_plate: string | null
  status: InspectionLeadStatus
  status_changed_at: string
  created_at: string
  /** PDF отчёта Автотеки выгружен (индикатор в списке). */
  has_autoteka: boolean
  /** Протокол торгов Carlink получен (индикатор в списке). */
  has_protocol: boolean
  /** Пути в приватных bucket'ах: по ним иконки в списке открывают просмотрщик. */
  autoteka_pdf_path: string | null
  protocol_path: string | null
}

export interface PartnerLeadsResult {
  items: PartnerLeadRow[]
  /** Сколько заявок под фильтром статуса (для «Показать ещё»). */
  total: number
  /** Заявок по статусам с учётом поиска — для фильтров. */
  counts: Partial<Record<InspectionLeadStatus, number>>
}

export interface PartnerLot {
  status: string | null
  is_final: boolean | null
  /** Когда мост Carlink впервые увидел черновик лота (carlink_events, DRAFT). */
  draft_at: string | null
  started_at: string | null
  finished_at: string | null
  lot_url: string | null
  protocol_path: string | null
  /** Когда протокол торгов получен от Carlink и сохранён. */
  protocol_at: string | null
  /** Стартовая цена торгов, руб. = гарантированная цена. */
  start_price: number | null
  /** Ставка лидера, руб.; после окончания торгов итоговая. null: ставок нет. */
  leader_summa: number | null
  /** Юрлицо лидера; null у физлица (ФИО партнёру не отдаём) или без ставок. */
  leader_company: string | null
}

export interface PartnerLead {
  id: string
  lead_no: number
  external_id: string | null
  created_at: string
  status: InspectionLeadStatus
  status_changed_at: string
  client_name: string | null
  client_phone: string | null
  location: string | null
  vehicle_brand: string | null
  vehicle_model: string | null
  vehicle_year: number | null
  vehicle_vin: string | null
  license_plate: string | null
  trade_in_target_vehicle: string | null
  inspection_address: string | null
  cancel_comment: string | null
  contacted_at: string | null
  /** Осмотр не проведён: когда и почему (заказ отменён или отказ). */
  inspection_failed_at: string | null
  inspection_failed_reason: string | null
  /** Комментарий Carlink о текущем состоянии и дата его последней правки. */
  partner_comment: string | null
  partner_comment_at: string | null
  inspection_at: string | null
  report_url: string | null
  /** PDF отчёта Автотеки в bucket autoteka-reports ({lead_id}/{report_id}.pdf). */
  autoteka_pdf_path: string | null
  /** PDF оценки CM Expert (bucket cm-expert-reports) и когда загружена. */
  cm_expert_pdf_path: string | null
  cm_expert_pdf_at: string | null
  lot: PartnerLot | null
  /** Когда заявка входила в этапы (журнал смены статуса), по времени. */
  history: { status: InspectionLeadStatus; at: string }[]
}

export class PartnerAccessError extends Error {}

function rpcError(message: string): Error {
  return message.includes('forbidden') ? new PartnerAccessError(message) : new Error(message)
}

export async function getPartnerMe(): Promise<PartnerMe> {
  if (USE_MOCK) return mockPartner.me()
  const { data, error } = await createClient().rpc('partner_me')
  if (error) throw rpcError(error.message)
  return data as PartnerMe
}

export async function listPartnerLeads(params: {
  search?: string
  status?: InspectionLeadStatus | null
  limit?: number
  offset?: number
}): Promise<PartnerLeadsResult> {
  if (USE_MOCK) return mockPartner.list(params)
  const { data, error } = await createClient().rpc('partner_leads', {
    p_search: params.search?.trim() || null,
    p_status: params.status ?? null,
    p_limit: params.limit ?? 50,
    p_offset: params.offset ?? 0,
  })
  if (error) throw rpcError(error.message)
  return data as PartnerLeadsResult
}

export async function getPartnerLead(id: string): Promise<PartnerLead> {
  if (USE_MOCK) return mockPartner.get(id)
  const { data, error } = await createClient().rpc('partner_lead', { p_id: id })
  if (error) throw rpcError(error.message)
  return data as PartnerLead
}

/** Завести заявку или дополнить существующую с тем же номером CRM. */
export async function upsertPartnerLead(
  payload: SberAutoLeadPayload,
): Promise<{ leadId: string; created: boolean }> {
  if (USE_MOCK) return mockPartner.upsert(payload)
  const { data, error } = await createClient().rpc('partner_lead_upsert', { p_data: payload })
  if (error) throw rpcError(error.message)
  const r = data as { lead_id: string; created: boolean }
  return { leadId: r.lead_id, created: r.created }
}

/** Заявка с таким номером CRM — для предупреждения «дополним существующую». */
export async function findPartnerLeadByExternalId(
  externalId: string,
): Promise<Pick<PartnerLeadRow, 'id' | 'client_name' | 'status'> | null> {
  const r = await listPartnerLeads({ search: externalId, limit: 20 })
  return r.items.find((l) => l.external_id === externalId) ?? null
}

/** Протокол торгов (bucket carlink-protocols) — ссылка на скачивание на 10 минут. */
export interface PartnerFileUrls {
  /** Открыть в браузере (inline, без Content-Disposition: attachment). */
  view: string
  /** Скачать под заданным именем файла. */
  download: string
}

/** Подписанная ссылка на 10 минут из приватного bucket: одна на просмотр,
 *  та же с `download=<имя>` на скачивание. */
async function partnerFileUrls(bucket: string, path: string, fileName: string): Promise<PartnerFileUrls> {
  if (USE_MOCK) return { view: 'about:blank', download: '#' }
  const { data, error } = await createClient().storage.from(bucket).createSignedUrl(path, 600)
  if (error || !data?.signedUrl) throw new Error(error?.message ?? 'no_url')
  const u = new URL(data.signedUrl)
  u.searchParams.set('download', fileName)
  return { view: data.signedUrl, download: u.toString() }
}

/** Протокол торгов (bucket carlink-protocols). Имя файла: «Lead <номер CRM>.pdf»,
 *  без номера CRM: по нашему номеру лида. */
export function partnerProtocolUrl(path: string, lead: Pick<PartnerLead, 'lead_no' | 'external_id'>): Promise<PartnerFileUrls> {
  return partnerFileUrls('carlink-protocols', path, `Lead ${lead.external_id ?? lead.lead_no}.pdf`)
}

/** Оценка CM Expert (bucket cm-expert-reports). */
export function partnerCmExpertUrl(path: string, leadNo: number): Promise<PartnerFileUrls> {
  return partnerFileUrls('cm-expert-reports', path, `cm-expert-${leadNo}.pdf`)
}

/** Отчёт Автотеки (bucket autoteka-reports). */
export function partnerAutotekaUrl(path: string, leadNo: number): Promise<PartnerFileUrls> {
  return partnerFileUrls('autoteka-reports', path, `autoteka-${leadNo}.pdf`)
}
